import { Schema, type, MapSchema } from '@colyseus/schema';

export class PowerupState extends Schema {
  @type('string') declare type: string; // 'bolt' | 'shield' | 'star'
  @type('string') declare tier: string; // 'green' | 'blue' | 'yellow' | 'red'
  @type('float32') declare x: number;
  @type('float32') declare y: number;
  /** Remaining lifetime in seconds — client uses this to compute despawn fade. */
  @type('float32') declare ttl: number;
}

export class SpaceStationState extends Schema {
  @type('float32') declare x: number;
  @type('float32') declare y: number;
  @type('float32') declare rotation: number;
  /** Absolute world positions of the two healing pads (computed from x/y/rotation). */
  @type('float32') declare pad0X: number;
  @type('float32') declare pad0Y: number;
  @type('float32') declare pad1X: number;
  @type('float32') declare pad1Y: number;
  /** Whether each pad is actively healing a ship (drives client glow intensity). */
  @type('boolean') declare pad0Healing: boolean;
  @type('boolean') declare pad1Healing: boolean;
}

export class PlayerState extends Schema {
  @type('string') declare sessionId: string;
  @type('string') declare shipId: string;
  @type('string') declare displayName: string;

  @type('float32') declare x: number;
  @type('float32') declare y: number;
  @type('float32') declare rotation: number; // radians, 0 = up
  @type('float32') declare velocityX: number;
  @type('float32') declare velocityY: number;

  @type('float32') declare armor: number;   // current HP
  @type('float32') declare shield: number;  // current shield strength
  @type('float32') declare energy: number;  // current energy

  @type('boolean') declare isDead: boolean;
  @type('uint16')  declare kills: number;
  @type('uint16')  declare deaths: number;

  /** Current secondary weapon magazine count (synced to client for HUD). */
  @type('uint8')   declare secondaryAmmo: number;

  /** True while the ship's main engines are firing (drives client flame FX). */
  @type('boolean') declare isThrusting: boolean;

  // Per-round stats (reset at round start)
  @type('uint16')  declare roundKills: number;
  @type('uint16')  declare roundDeaths: number;
  @type('float32') declare damageDealt: number;
  @type('float32') declare damageReceived: number;
  /** Longest consecutive alive streak this round, in seconds. Finalised on death or round end. */
  @type('float32') declare longestStreak: number;
  /** Running alive streak this round, in seconds. Resets to 0 on death. */
  @type('float32') declare currentStreak: number;

  /** Accumulated rank points this round (resets each round). */
  @type('uint16')  declare rankPoints: number;
  /** Current rank index: 0=Cadet 1=Ensign 2=Lieutenant 3=Captain 4=Admiral. */
  @type('uint8')   declare rankIndex: number;
}

/**
 * A live projectile travelling through the world.
 * Beam weapons are instant (no projectile state) — they fire via a broadcast event.
 */
export class ProjectileState extends Schema {
  @type('string')  declare ownerSessionId: string;
  @type('float32') declare x: number;
  @type('float32') declare y: number;
  /** Constant velocity vector — also used by the client for dead-reckoning. */
  @type('float32') declare velocityX: number;
  @type('float32') declare velocityY: number;
  /** Accumulated travel distance, used by client to compute brightness fade. */
  @type('float32') declare distanceTraveled: number;
  /** Max range for this shot (from weapon definition at fire time). */
  @type('float32') declare maxRange: number;
  /** Weapon type: 'pulse' | 'torpedo' | 'missile' — drives client rendering. */
  @type('string')  declare weaponType: string;
  /** Guidance turn rate in rad/s. 0 = unguided (pulse bolts). */
  @type('float32') declare homingStrength: number;
}

export class GameState extends Schema {
  @type({ map: PlayerState })          players     = new MapSchema<PlayerState>();
  @type({ map: ProjectileState })      projectiles = new MapSchema<ProjectileState>();
  @type({ map: SpaceStationState })    stations    = new MapSchema<SpaceStationState>();
  @type({ map: PowerupState })         powerups    = new MapSchema<PowerupState>();
  /** 'waiting' | 'playing' | 'roundEnd' */
  @type('string')  declare roundPhase: string;
  @type('uint32')  declare tickNumber: number;
  @type('uint16')  declare roundNumber: number;
  /** During 'playing': seconds left in round. During 'roundEnd': seconds until next round. */
  @type('float32') declare roundTimeRemaining: number;
  /** Round length in seconds, as configured at room creation. */
  @type('uint16')  declare roundDuration: number;
  /** Display name for this room, shown under the round timer. */
  @type('string')  declare roomName: string;
}
